<script setup lang="ts">
/**
 * Three-step signup progress strip above the auth panel.
 * Email → Details → Authorise. Details can be revisited from Authorise.
 * Icons match dashboard Account (profile) and Swiftaid status (shield).
 */
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'
import type { DashboardNavIconName } from '@/helpers/dashboardNavIcons'
import { LANDING_PAGE } from '@/types'

const props = withDefaults(
  defineProps<{
    /** 1 Email → 2 Details → 3 Authorise */
    currentStep: 1 | 2 | 3
  }>(),
  {
    currentStep: 1,
  }
)

const store = useStore()
const router = useRouter()

const steps: {
  id: 1 | 2 | 3
  labelKey: string
  icon: DashboardNavIconName
}[] = [
  {
    id: 1,
    labelKey: 'ManageAccount.Signup.Steps.Email',
    icon: 'email',
  },
  {
    id: 2,
    labelKey: 'ManageAccount.Signup.Steps.Details',
    icon: 'settings',
  },
  {
    id: 3,
    labelKey: 'ManageAccount.Signup.Steps.Authorise',
    icon: 'matching',
  },
]

const stepClass = (stepId: 1 | 2 | 3, current: 1 | 2 | 3) => {
  if (stepId === current) return 'is-current'
  if (stepId < current) return 'is-done'
  return undefined
}

/** Details is revisitable once the donor reaches Authorise. */
const isStepClickable = (stepId: 1 | 2 | 3) =>
  stepId === 2 && props.currentStep === 3

const onStepActivate = (stepId: 1 | 2 | 3) => {
  if (!isStepClickable(stepId)) return
  // Reset landing so create-details isn’t bounced straight back to authorise.
  store.commit('SET_LANDING_PAGE', LANDING_PAGE.NAME_ADDRESS)
  router.push({ name: 'DONOR_FLOW_ManageAccountCreateDetails' })
}
</script>

<template>
  <ol
    class="sa-auth-steps sa-auth-steps--rail"
    :aria-label="$t('ManageAccount.Signup.Steps.Label')"
  >
    <li
      v-for="step in steps"
      :key="step.id"
      class="sa-auth-steps__item"
      :class="[
        stepClass(step.id, currentStep),
        { 'sa-auth-steps__item--clickable': isStepClickable(step.id) },
      ]"
      :aria-current="step.id === currentStep ? 'step' : undefined"
    >
      <button
        v-if="isStepClickable(step.id)"
        type="button"
        class="sa-auth-steps__button"
        :aria-label="$t('ManageAccount.Signup.Steps.GoToDetailsAria')"
        @click="onStepActivate(step.id)"
      >
        <span class="sa-auth-steps__index" aria-hidden="true">
          <DashboardNavIcon :name="step.icon" />
        </span>
        <span>{{ $t(step.labelKey) }}</span>
      </button>
      <template v-else>
        <span class="sa-auth-steps__index" aria-hidden="true">
          <DashboardNavIcon :name="step.icon" />
        </span>
        <span>
          {{ $t(step.labelKey) }}
          <span v-if="step.id < currentStep" class="sa-sr-only">
            {{ $t('ManageAccount.Signup.Steps.Completed') }}
          </span>
        </span>
      </template>
    </li>
  </ol>
</template>
